import { Controller, ControllerProps, useFormContext } from 'react-hook-form';
import { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import Grid from '@mui/material/Grid2';
import Typography from '@mui/material/Typography';
import parse from 'autosuggest-highlight/parse';
import { debounce } from '@mui/material/utils';
import { Loader } from '@googlemaps/js-api-loader';
import { GOOGLE_MAPS_API_KEY } from '@modules/config';
import { log } from '@utils/logging';
import { EventForm } from '@components/eventCreation/CreateEventUtils';

const loader = new Loader({
    apiKey: GOOGLE_MAPS_API_KEY,
    version: 'weekly',
    libraries: ['maps', 'places'],
});

// load google maps async, after the app has loaded
loader
    .load()
    .then(
        (
            // eslint-disable-next-line no-empty-pattern
            {
                /* Map */
            },
        ) => {
            console.log(`Google Maps Loaded`);
        },
    )
    .catch((e) => {
        log(`Error loading Google Maps: ${e}`);
    });

const autocompleteService = { current: null };

interface MainTextMatchedSubstrings {
    offset: number;
    length: number;
}
interface StructuredFormatting {
    main_text: string;
    secondary_text: string;
    main_text_matched_substrings?: readonly MainTextMatchedSubstrings[];
}
interface PlaceType {
    description: string;
    structured_formatting: StructuredFormatting;
    place_id?: string;
}

// Got this from: https://mui.com/material-ui/react-autocomplete/#google-maps-place
export interface FormInputProps extends Omit<ControllerProps, 'name' | 'control' | 'render'> {
    editable?: boolean;
    name: keyof EventForm;
    prefixFormLookup?: string;
}

export default function FormAutoCompleteLocation({
    editable = true,
    name,
    prefixFormLookup = '',
    ...rest
}: FormInputProps) {
    const placeIdKey = `${prefixFormLookup}placeId` as keyof EventForm;
    const {
        formState: { errors },
        setValue: setFormValue,
        trigger,
        watch,
    } = useFormContext<EventForm>();

    const [location] = watch([name]);

    const [value, setValue] = useState<PlaceType | null>(null);
    const [inputValue, setInputValue] = useState('');
    const [options, setOptions] = useState<readonly PlaceType[]>([]);

    const fetch = useMemo(
        () =>
            debounce(
                (
                    request: { input: string },
                    callback: (results?: readonly PlaceType[]) => void,
                ) => {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    (autocompleteService.current as any).getPlacePredictions(request, callback);
                },
                400,
            ),
        [],
    );

    useEffect(() => {
        let active = true;

        if (!autocompleteService.current && window?.google) {
            autocompleteService.current =
                new // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (window as any).google.maps.places.AutocompleteService();
        }
        if (!autocompleteService.current) {
            return undefined;
        }

        if (inputValue === '') {
            setOptions(value ? [value] : []);
            return undefined;
        }

        fetch({ input: inputValue }, (results?: readonly PlaceType[]) => {
            if (active) {
                let newOptions: readonly PlaceType[] = [];

                if (value) {
                    newOptions = [value];
                }

                if (results) {
                    newOptions = [...newOptions, ...results];
                }

                setOptions(newOptions);
            }
        });

        return () => {
            active = false;
        };
    }, [value, inputValue, fetch]);

    return (
        <Controller
            {...rest}
            name={name}
            render={({ field: { onChange, value, ...restField } /*, fieldState: { error } */ }) =>
                editable ? (
                    <Autocomplete
                        {...restField}
                        autoComplete
                        filterOptions={(x) => x}
                        filterSelectedOptions
                        getOptionLabel={(option) =>
                            typeof option === 'string' ? option : option.description
                        }
                        includeInputInList
                        noOptionsText="No locations"
                        onChange={(_event: unknown, newValue: PlaceType | null) => {
                            setOptions(newValue ? [newValue, ...options] : options);
                            setValue(newValue);
                            onChange(newValue?.description || '');
                            setFormValue(placeIdKey, newValue?.place_id || '', {
                                shouldDirty: true,
                                shouldTouch: true,
                                shouldValidate: true,
                            });
                            // triggering Yup validation on change
                            trigger?.(name);
                            trigger?.(placeIdKey);
                        }}
                        onInputChange={(_event, newInputValue) => {
                            setInputValue(newInputValue);
                        }}
                        options={options}
                        sx={{ width: 300 }}
                        value={value}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Add a location"
                                fullWidth
                                error={
                                    errors['location']?.message || errors[placeIdKey]?.message
                                        ? true
                                        : false
                                }
                                helperText={
                                    errors['location']?.message || errors[placeIdKey]?.message ? (
                                        <Typography color="error" variant="caption">
                                            {errors['location']?.message ||
                                                errors[placeIdKey]?.message}
                                        </Typography>
                                    ) : null
                                }
                                required
                            />
                        )}
                        renderOption={(elementProps, option) => {
                            const { key, ...optionProps } = elementProps;
                            const matches: { length: number; offset: number }[] =
                                option.structured_formatting.main_text_matched_substrings || [];

                            const parts = parse(
                                option.structured_formatting.main_text,
                                matches.map((match) => [match.offset, match.offset + match.length]),
                            );
                            return (
                                <li key={key} {...optionProps}>
                                    <Grid container sx={{ alignItems: 'center' }}>
                                        <Grid sx={{ display: 'flex', width: 44 }}>
                                            <LocationOnIcon sx={{ color: 'text.secondary' }} />
                                        </Grid>
                                        <Grid
                                            sx={{
                                                width: 'calc(100% - 44px)',
                                                wordWrap: 'break-word',
                                            }}
                                        >
                                            {parts.map((part, index) => (
                                                <Box
                                                    key={index}
                                                    component="span"
                                                    sx={{
                                                        fontWeight: part.highlight
                                                            ? 'bold'
                                                            : 'regular',
                                                    }}
                                                >
                                                    {part.text}
                                                </Box>
                                            ))}
                                            <Typography
                                                variant="body2"
                                                sx={{ color: 'text.secondary' }}
                                            >
                                                {option.structured_formatting.secondary_text}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </li>
                            );
                        }}
                    />
                ) : (
                    <Typography variant="body1" className="value">
                        {typeof location === 'string' ? location : JSON.stringify(location)}
                    </Typography>
                )
            }
        />
    );
}
